import { useEffect, useState } from "react";
import { Users2, Search, Building2, User, Landmark, X, Mail, Phone, ShoppingBag, Tags, Plus, Pencil, Trash2, Loader2, Percent, Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import AccountRequests from "@/pages/admin/AccountRequests";

const eur = (n) => `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");

const TYPE = {
  particulier: { label: "Particulier", icon: User, cls: "bg-brand/10 text-brand" },
  entreprise: { label: "Entreprise", icon: Building2, cls: "bg-terracotta/10 text-terracotta" },
  collectivite: { label: "Collectivité", icon: Landmark, cls: "bg-indigo-100 text-indigo-700" },
};
const STATUS = {
  actif: { label: "Actif", cls: "bg-green-100 text-green-700" },
  approuve: { label: "Pro approuvé", cls: "bg-green-100 text-green-700" },
  en_attente: { label: "En attente", cls: "bg-amber-100 text-amber-700" },
  incomplet: { label: "Incomplet", cls: "bg-stone/15 text-stone" },
  refuse: { label: "Refusé", cls: "bg-red-100 text-red-700" },
};

function CustomerModal({ id, groups, onClose, onChanged }) {
  const [c, setC] = useState(null);
  const [groupId, setGroupId] = useState("");
  const [savingGroup, setSavingGroup] = useState(false);
  useEffect(() => { api.get(`/customers/${id}`).then((r) => { setC(r.data); setGroupId(r.data.group_id || ""); }); }, [id]);
  if (!c) return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose} data-testid="customer-modal"><div className="text-cream">Chargement…</div></div>
  );
  const t = TYPE[c.account_type] || TYPE.particulier;
  const st = STATUS[c.account_status] || { label: c.account_status, cls: "bg-stone/15 text-stone" };
  const isPro = c.account_type !== "particulier";
  const saveGroup = async () => {
    setSavingGroup(true);
    try { await api.put(`/customers/${id}/group`, { group_id: groupId || null }); toast.success("Groupe mis à jour"); onChanged && onChanged(); }
    catch { toast.error("Erreur"); } finally { setSavingGroup(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4 sm:p-8" onClick={onClose} data-testid="customer-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-brand/10 flex items-center justify-center"><t.icon size={20} className="text-brand" /></div>
            <div>
              <h3 className="font-semibold text-forest">{c.name}{c.company ? ` · ${c.company}` : ""}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${t.cls}`}>{t.label}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                {c.group_name && <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-brand/10 text-brand">{c.group_name}</span>}
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="customer-modal-close" className="p-2 rounded-lg hover:bg-sage/50"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="rounded-lg bg-[#F0EFEA] p-3 text-center"><p className="font-display text-2xl text-forest">{c.orders_count}</p><p className="text-xs text-stone">Commandes</p></div>
            <div className="rounded-lg bg-[#F0EFEA] p-3 text-center"><p className="font-display text-2xl text-brand">{eur(c.total_spent)}</p><p className="text-xs text-stone">Total dépensé</p></div>
            <div className="rounded-lg bg-[#F0EFEA] p-3 text-center"><p className="font-display text-2xl text-forest">{fmt(c.created_at)}</p><p className="text-xs text-stone">Client depuis</p></div>
          </div>

          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div className="flex items-center gap-2 text-stone"><Mail size={14} className="text-brand" /> {c.email}</div>
            {c.phone && <div className="flex items-center gap-2 text-stone"><Phone size={14} className="text-brand" /> {c.phone}</div>}
            {isPro && c.siret && <div className="text-stone">SIRET : <span className="text-forest">{c.siret}</span></div>}
            {isPro && <div className="text-stone">Paiement : <span className="text-forest">{c.payment_terms === "30j_fin_de_mois" ? "30j fin de mois" : "Immédiat"}</span></div>}
            {isPro && c.payment_terms === "30j_fin_de_mois" && <div className="text-stone">Encours : <span className="text-forest">{eur(c.credit_limit)} HT</span></div>}
            {c.billing_address?.address && <div className="text-stone sm:col-span-2">Facturation : <span className="text-forest">{c.billing_address.address}, {c.billing_address.postal_code} {c.billing_address.city}</span></div>}
          </div>

          {/* Groupe tarifaire */}
          <div className="rounded-xl border border-brand/30 bg-brand/5 p-4">
            <p className="text-xs font-bold text-brand uppercase tracking-wide mb-2 flex items-center gap-1.5"><Tags size={13} /> Groupe tarifaire</p>
            <div className="flex gap-2">
              <select value={groupId} onChange={(e) => setGroupId(e.target.value)} data-testid="customer-group-select" className="flex-1 rounded-lg border border-border px-3 py-2 text-sm bg-white">
                <option value="">— Aucun groupe —</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <button onClick={saveGroup} disabled={savingGroup} data-testid="customer-group-save" className="rounded-lg bg-brand text-cream px-4 text-sm font-semibold hover:bg-forest flex items-center gap-1.5 disabled:opacity-60">{savingGroup ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Appliquer</button>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-stone uppercase tracking-wide mb-2 flex items-center gap-1.5"><ShoppingBag size={13} /> Commandes ({c.orders?.length || 0})</p>
            {(!c.orders || c.orders.length === 0) ? <p className="text-sm text-stone">Aucune commande.</p> : (
              <div className="divide-y divide-border border border-border rounded-xl">
                {c.orders.map((o) => (
                  <a key={o.id} href={`/admin/commandes/${o.order_id}`} className="flex items-center justify-between px-3 py-2.5 text-sm hover:bg-[#F7F7F8]">
                    <span className="text-forest">#{String(o.order_id).slice(0, 8).toUpperCase()} · {fmt(o.created_at)}</span>
                    <span className="flex items-center gap-2"><span className="text-[10px] px-2 py-0.5 rounded-full bg-stone/15 text-stone">{o.status}</span><span className="font-semibold text-forest">{eur(o.total)}</span></span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ClientsList({ groups, reloadGroups }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/customers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const counts = {
    all: rows.length,
    particulier: rows.filter((c) => c.account_type === "particulier").length,
    entreprise: rows.filter((c) => c.account_type === "entreprise").length,
    collectivite: rows.filter((c) => c.account_type === "collectivite").length,
  };
  const filtered = rows.filter((c) => {
    if (filter !== "all" && c.account_type !== filter) return false;
    if (q) {
      const s = q.toLowerCase();
      return (c.name || "").toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s) || (c.company || "").toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div>
      <p className="text-stone mb-4">{counts.all} client(s) · {counts.particulier} particulier(s) · {counts.entreprise} entreprise(s) · {counts.collectivite} collectivité(s)</p>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom, email, société)" data-testid="customer-search" className="w-full rounded-lg border border-border pl-9 pr-3 py-2 text-sm" />
        </div>
        <div className="inline-flex rounded-full bg-white border border-border p-1" data-testid="customer-filter">
          {[["all", "Tous"], ["particulier", "Particuliers"], ["entreprise", "Entreprises"], ["collectivite", "Collectivités"]].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === v ? "bg-brand text-white" : "text-stone hover:text-forest"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="rounded-xl bg-white border border-border overflow-hidden">
        {filtered.length === 0 ? <p className="text-stone text-sm p-10 text-center">Aucun client.</p> : (
          <div className="divide-y divide-border">
            {filtered.map((c) => {
              const t = TYPE[c.account_type] || TYPE.particulier;
              const st = STATUS[c.account_status] || { label: c.account_status, cls: "bg-stone/15 text-stone" };
              return (
                <button key={c.id} onClick={() => setSelected(c.id)} data-testid={`customer-${c.id}`} className="w-full flex items-center gap-4 px-5 py-3.5 text-left hover:bg-[#F7F7F8] transition-colors">
                  <div className="h-10 w-10 rounded-full bg-brand/10 flex items-center justify-center shrink-0"><t.icon size={18} className="text-brand" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-forest truncate">{c.name}{c.company ? ` · ${c.company}` : ""}</p>
                    <p className="text-xs text-stone truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                  </div>
                  {c.group_name && <span className="text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 hidden lg:inline bg-brand/10 text-brand">{c.group_name}</span>}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 hidden sm:inline ${t.cls}`}>{t.label}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 hidden md:inline ${st.cls}`}>{st.label}</span>
                  <div className="text-right shrink-0 w-24 hidden sm:block">
                    <p className="text-sm font-semibold text-forest">{eur(c.total_spent)}</p>
                    <p className="text-xs text-stone">{c.orders_count} cmd</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {selected && <CustomerModal id={selected} groups={groups} onClose={() => setSelected(null)} onChanged={() => { load(); reloadGroups(); }} />}
    </div>
  );
}

const emptyGroup = { name: "", description: "", client_type: "all", rules: [] };

function GroupsManager({ groups, reload }) {
  const [editing, setEditing] = useState(null); // group id or "new"
  const [form, setForm] = useState(emptyGroup);
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const startNew = () => { setForm({ ...emptyGroup, rules: [] }); setEditing("new"); };
  const startEdit = (g) => { setForm({ name: g.name, description: g.description || "", client_type: g.client_type || "all", rules: g.rules || [] }); setEditing(g.id); };
  const cancel = () => { setEditing(null); setForm(emptyGroup); };

  const addRule = () => setForm((f) => ({ ...f, rules: [...f.rules, { scope: "all", target: "", percent: 10 }] }));
  const setRule = (i, k, v) => setForm((f) => { const r = [...f.rules]; r[i] = { ...r[i], [k]: v }; if (k === "scope") r[i].target = ""; return { ...f, rules: r }; });
  const delRule = (i) => setForm((f) => ({ ...f, rules: f.rules.filter((_, k) => k !== i) }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom du groupe requis"); return; }
    setSaving(true);
    const payload = { ...form, rules: form.rules.map((r) => ({ scope: r.scope, target: r.target || "", percent: Number(r.percent || 0) })) };
    try {
      if (editing === "new") await api.post("/customer-groups", payload);
      else await api.put(`/customer-groups/${editing}`, payload);
      toast.success("Groupe enregistré"); cancel(); reload();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce groupe ? Les clients seront détachés.")) return;
    await api.delete(`/customer-groups/${id}`); toast.success("Groupe supprimé"); reload();
  };

  const input = "w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30";

  return (
    <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
      {/* List */}
      <div>
        <button onClick={startNew} data-testid="group-new-btn" className="mb-4 w-full rounded-full bg-brand text-cream py-2.5 font-semibold hover:bg-forest flex items-center justify-center gap-1.5"><Plus size={16} /> Nouveau groupe</button>
        {groups.length === 0 ? <p className="text-stone text-sm">Aucun groupe. Créez-en un pour appliquer des tarifs préférentiels.</p> : (
          <div className="space-y-3">
            {groups.map((g) => (
              <div key={g.id} className="rounded-xl bg-white border border-border p-4" data-testid={`group-${g.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-forest">{g.name} <span className="text-xs text-stone">· {g.members_count} client(s)</span></p>
                    {g.description && <p className="text-xs text-stone">{g.description}</p>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(g)} data-testid={`group-edit-${g.id}`} className="text-stone hover:text-brand p-1.5"><Pencil size={15} /></button>
                    <button onClick={() => remove(g.id)} data-testid={`group-delete-${g.id}`} className="text-red-500 hover:bg-red-50 rounded p-1.5"><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(g.rules || []).map((r, i) => (
                    <span key={i} className="text-[11px] rounded-full bg-brand/10 text-brand px-2 py-0.5 font-medium">
                      -{r.percent}% {r.scope === "all" ? "tout le catalogue" : r.scope === "category" ? `cat. ${r.target}` : `produit`}
                    </span>
                  ))}
                  {(g.rules || []).length === 0 && <span className="text-xs text-stone">Aucune remise définie</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Editor */}
      {editing && (
        <div className="rounded-xl bg-white border border-border p-6 h-fit" data-testid="group-editor">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-forest">{editing === "new" ? "Nouveau groupe" : "Modifier le groupe"}</h3>
            <button onClick={cancel} className="text-stone hover:text-forest"><X size={18} /></button>
          </div>
          <div className="space-y-3">
            <input className={input} placeholder="Nom du groupe (ex: Premium, Contrats d'entretien)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="group-name" />
            <input className={input} placeholder="Description (optionnel)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="group-desc" />
            <div>
              <label className="text-xs text-stone">Type de clientèle visée</label>
              <select className={input} value={form.client_type} onChange={(e) => setForm({ ...form, client_type: e.target.value })} data-testid="group-clienttype">
                <option value="all">Tous (particuliers & pros)</option>
                <option value="particulier">Particuliers</option>
                <option value="entreprise">Entreprises</option>
              </select>
            </div>

            <div className="rounded-lg border border-border bg-[#F0EFEA] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-forest flex items-center gap-1.5"><Percent size={13} /> Remises</p>
                <button onClick={addRule} data-testid="group-add-rule" className="text-xs font-semibold text-brand hover:underline flex items-center gap-1"><Plus size={13} /> Ajouter une remise</button>
              </div>
              {form.rules.length === 0 && <p className="text-xs text-stone">Aucune remise. Ajoutez-en une (ex: -10% sur tout le catalogue).</p>}
              {form.rules.map((r, i) => (
                <div key={i} className="rounded-lg bg-white border border-border p-3 space-y-2" data-testid={`rule-${i}`}>
                  <div className="flex items-center gap-2">
                    <select className={input} value={r.scope} onChange={(e) => setRule(i, "scope", e.target.value)} data-testid={`rule-scope-${i}`}>
                      <option value="all">Tout le catalogue</option>
                      <option value="category">Une catégorie</option>
                      <option value="product">Un produit</option>
                    </select>
                    <div className="flex items-center gap-1 shrink-0">
                      <input type="number" step="1" min="0" max="100" className="w-20 rounded-lg border border-border px-2 py-2 text-sm text-right" value={r.percent} onChange={(e) => setRule(i, "percent", e.target.value)} data-testid={`rule-percent-${i}`} />
                      <span className="text-sm text-stone">%</span>
                    </div>
                    <button onClick={() => delRule(i)} className="text-red-500 hover:bg-red-50 rounded p-1.5 shrink-0"><Trash2 size={15} /></button>
                  </div>
                  {r.scope === "category" && (
                    <select className={input} value={r.target} onChange={(e) => setRule(i, "target", e.target.value)} data-testid={`rule-cat-${i}`}>
                      <option value="">— Choisir une catégorie —</option>
                      {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  )}
                  {r.scope === "product" && (
                    <select className={input} value={r.target} onChange={(e) => setRule(i, "target", e.target.value)} data-testid={`rule-prod-${i}`}>
                      <option value="">— Choisir un produit —</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  )}
                </div>
              ))}
            </div>
            <button onClick={save} disabled={saving} data-testid="group-save" className="w-full rounded-full bg-brand text-cream py-3 font-semibold hover:bg-forest flex items-center justify-center gap-2 disabled:opacity-60">{saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Enregistrer le groupe</button>
          </div>
        </div>
      )}
    </div>
  );
}

const TABS = [["clients", "Clients"], ["pro", "Demandes d'ouverture de compte pro"], ["groupes", "Groupes & tarifs"]];

export default function Customers() {
  const [tab, setTab] = useState("clients");
  const [groups, setGroups] = useState([]);
  const reloadGroups = () => api.get("/customer-groups").then((r) => setGroups(r.data)).catch(() => {});
  useEffect(() => { reloadGroups(); }, []);

  return (
    <div>
      <h1 className="font-display text-4xl text-forest mb-4 flex items-center gap-2"><Users2 className="text-brand" /> Clients</h1>
      <div className="flex flex-wrap gap-2 mb-6 border-b border-border">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`clients-tab-${v}`}
            className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === v ? "border-brand text-brand" : "border-transparent text-stone hover:text-forest"}`}>{l}</button>
        ))}
      </div>

      {tab === "clients" && <ClientsList groups={groups} reloadGroups={reloadGroups} />}
      {tab === "pro" && <AccountRequests />}
      {tab === "groupes" && <GroupsManager groups={groups} reload={reloadGroups} />}
    </div>
  );
}
